import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Clock, Target, CheckCircle2, Loader2, AlertTriangle,
  FileDown, FileSpreadsheet, Building2, MapPin, Users, Search,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEventsByRange, searchCompanies, getWorkplaces, getCompanyWorkers,
  searchWorkers, getWorkHours, jsDowToBackend, EVENT_TYPES,
} from '../services/api';
import { can, USER_TYPES, isSedeScoped, filterWorkersForUser, workerSites } from '../lib/permissions';
import { generateBulkRangePdf, generateBulkRangeExcel, generateBulkRangeExcelDetailed } from '../services/reports';
import { buildWorkHoursByDow, countDaysInRange } from '../lib/vacationCounter';
import { filterEventsToLocalRange, buildDayStatusSets } from '../lib/dayStatus';
import InformeHorarioView from '../components/InformeHorarioView';
import HourBalanceCard from '../components/HourBalanceCard';
import { useHolidays } from '../hooks/useHolidays';
import { useWorkerSchedule, balanceEventsRange } from '../hooks/useWorkerHourBalance';
import { balanceHolidayYears, computeDayWork } from '../lib/hourBalance';
import {
  timeZoneForWorker, dayKeyInZone, minutesOfDayInZone, todayInZone, addDaysToDayKey, dowOfDayKey,
} from '../lib/workerTime';

// ─────────────────────────────────────────────────────────────
// Helpers compartidos
// ─────────────────────────────────────────────────────────────
const fmtHours = (mins) => !mins ? '0h 00m' : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;

// ─────────────────────────────────────────────────────────────
// Actividad personal — lógica existente
// ─────────────────────────────────────────────────────────────
const PERIODS = [
  { key: 'semana', label: 'Última semana' },
  { key: 'mes',    label: 'Este mes' },
  { key: 'año',    label: 'Este año' },
];

const TOOLTIP_STYLE = { borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12, boxShadow: '0 4px 12px rgba(15,23,42,0.08)' };

// Rango del período como CLAVES de día ('YYYY-MM-DD') en la zona del
// trabajador: [inicio de semana/mes/año, hoy]. Aritmética pura de claves.
const getRangeFor = (period, tz) => {
  const end = todayInZone(tz);
  let start = end;
  if (period === 'semana') {
    start = addDaysToDayKey(end, -((dowOfDayKey(end) + 6) % 7));
  } else if (period === 'mes') {
    start = `${end.slice(0, 8)}01`;
  } else if (period === 'año') {
    start = `${end.slice(0, 5)}01-01`;
  }
  return { start, end };
};

// 'YYYY-MM' siguiente.
const nextMonthKey = (ym) => {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
};

// Etiqueta del eje a partir de una clave de día (se pinta en UTC para que el
// navegador no la desplace).
const dayLabel = (key, period) => {
  const d = new Date(`${key}T00:00:00Z`);
  if (period === 'semana') return d.toLocaleDateString('es-ES', { weekday: 'short', timeZone: 'UTC' });
  if (period === 'año') return d.toLocaleDateString('es-ES', { month: 'short', timeZone: 'UTC' });
  return String(Number(key.slice(8, 10)));
};


const PersonalActivity = ({ user }) => {
  const [period, setPeriod] = useState('semana');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workhoursByDow, setWorkhoursByDow] = useState(new Map());
  // Horario para la Bolsa de horas: histórico de versiones ENRIQUECIDO (cada
  // día se mide contra el horario vigente ese día, no contra el de hoy) +
  // versión virtual con el horario actual. Mismo helper que la ficha del
  // trabajador (WorkerDetailPage) y que la app, así el saldo coincide.
  const { scheduleInput, loading: scheduleLoading } = useWorkerSchedule(user?.id);
  // Zona del propio trabajador: días, "hoy" y horas de reloj en SU hora.
  const tz = useMemo(
    () => timeZoneForWorker(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.autonomousCommunity, user?.province]
  );
  // Festivos de TODOS los años del cómputo de la bolsa (no solo el actual).
  const { holidays: myHolidays } = useHolidays(user, balanceHolidayYears(new Date(), tz));

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    // Mismo rango que la ficha del trabajador (1-ene del año pasado → 31-dic
    // del actual). La bolsa lo necesita tal cual: su cómputo termina en
    // min(último evento, ayer), así que un rango distinto da otro saldo. Las
    // métricas del período filtran por fechas y no se ven afectadas.
    const { start, end } = balanceEventsRange(new Date(), tz);
    try {
      const [data, slots] = await Promise.all([
        // start - 1: el día de Madrid empieza a las 22:00/23:00 UTC del anterior.
        getEventsByRange(user.id, addDaysToDayKey(start, -1), end),
        getWorkHours(user.id).catch(() => []),
      ]);
      setEvents(Array.isArray(data) ? data : []);
      const arr = Array.isArray(slots) ? slots : [];
      const map = new Map();
      for (const s of arr) map.set(Number(s.dayOfWeek), s);
      setWorkhoursByDow(map);
    } catch (err) {
      setError(err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, tz]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    // Rango y días en la zona del trabajador (claves 'YYYY-MM-DD'). Como
    // antes, solo cuenta lo ya ocurrido (hasta ahora).
    const { start, end } = getRangeFor(period, tz);
    const nowMs = Date.now();
    const periodEvents = events.filter((e) => {
      const k = dayKeyInZone(e.timestamp, tz);
      return k && k >= start && k <= end && new Date(e.timestamp).getTime() <= nowMs;
    });

    const byDay = {};
    for (const e of periodEvents) {
      const k = dayKeyInZone(e.timestamp, tz);
      if (!byDay[k]) byDay[k] = { clockIns: [], clockOuts: [], all: [] };
      byDay[k].all.push(e);
      if (e.eventType === EVENT_TYPES.CLOCK_IN) byDay[k].clockIns.push(e.timestamp);
      if (e.eventType === EVENT_TYPES.CLOCK_OUT) byDay[k].clockOuts.push(e.timestamp);
    }
    // Vacaciones / día completo del reparto: TODOS los días de los periodos
    // APROBADOS (dayStatus.buildDayStatusSets, mismo emparejado que el
    // calendario) dentro del rango y hasta hoy. Antes solo se marcaban los
    // días con un evento START/END y contaban también los rechazados (5 días
    // en vez de 15 para un caso real).
    const statusSets = buildDayStatusSets(events, tz);
    const vacDays = countDaysInRange(statusSets.vacationApproved, start, end);
    const sickDays = countDaysInRange(statusSets.sickApproved, start, end);

    let totalMinutes = 0;
    let punctualDays = 0;
    let countedDays = 0;
    let completeDays = 0;
    let expectedMinutes = 0;

    // Para cada día, calculamos su startMin y duración esperada según el
    // slot de /api/workhours correspondiente al dayOfWeek. Si el día es
    // descanso (sin slot) o libre, no participa en puntualidad ni en
    // expectedMinutes — toda la jornada de ese día cuenta como "extra".
    const parseHM = (hms) => {
      if (!hms) return null;
      const [h, m] = String(hms).slice(0, 5).split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const slotStatsForDate = (dateStr) => {
      const dow = jsDowToBackend(dowOfDayKey(dateStr));
      const slot = workhoursByDow.get(dow);
      if (!slot) return { startMin: null, expectedMin: 0 };
      const in1 = parseHM(slot.in1), out1 = parseHM(slot.out1);
      const in2 = parseHM(slot.in2), out2 = parseHM(slot.out2);
      const dur1 = in1 != null && out1 != null ? Math.max(0, out1 - in1) : 0;
      const dur2 = in2 != null && out2 != null ? Math.max(0, out2 - in2) : 0;
      const expectedMin = dur1 + dur2;
      const startCandidates = [in1, in2].filter((v) => v != null);
      const startMin = startCandidates.length ? Math.min(...startCandidates) : null;
      return { startMin, expectedMin };
    };

    // Ya NO calculamos aquí las "horas extra". Aquel cómputo era engañoso:
    // clampeaba el déficit a 0 con Math.max (nunca podía mostrar horas a
    // deber) y sólo miraba los días CON fichajes (los días no fichados no
    // restaban). El saldo real lo da ahora <HourBalanceCard>, con el MISMO
    // cómputo que la ficha del trabajador (computeAutoBalance): festivos,
    // vacaciones, días sin fichar, versiones de horario y ajustes manuales.
    for (const k of Object.keys(byDay)) {
      const d = byDay[k];
      d.clockIns.sort();
      d.clockOuts.sort();
      // Mismo cómputo que la bolsa y el resto de pantallas: TODAS las pausas
      // se restan y los fichajes rechazados no cuentan.
      d.workedMinutes = computeDayWork(d.all, { timeZone: tz }).workedMinutes;
      if (d.workedMinutes > 0) {
        totalMinutes += d.workedMinutes;
        if (d.clockOuts.length > 0) completeDays++;
      }
      const { startMin } = slotStatsForDate(k);
      d._startMin = startMin;
      if (d.clockIns[0] && startMin != null) {
        // Hora de reloj del trabajador (su horario está en su zona).
        const ciMinutes = minutesOfDayInZone(d.clockIns[0], tz);
        if (ciMinutes <= startMin + 5) punctualDays++;
        countedDays++;
      }
    }

    const puntualidad = countedDays > 0 ? Math.round((punctualDays / countedDays) * 100) : 100;

    // Ejes recorridos con CLAVES de día / mes (aritmética pura, sin la zona
    // del navegador).
    const chartDays = [];
    const unit = period === 'año' ? 'month' : 'day';
    const monthsOfRange = [];
    if (unit === 'month') {
      for (let ym = start.slice(0, 7); ym <= end.slice(0, 7); ym = nextMonthKey(ym)) monthsOfRange.push(ym);
      for (const ym of monthsOfRange) {
        let monthMinutes = 0;
        for (const k of Object.keys(byDay)) {
          if (k.slice(0, 7) === ym) monthMinutes += byDay[k].workedMinutes || 0;
        }
        chartDays.push({ x: dayLabel(`${ym}-01`, period), horas: Number((monthMinutes / 60).toFixed(1)) });
      }
    } else {
      for (let k = start; k && k <= end; k = addDaysToDayKey(k, 1)) {
        const m = byDay[k]?.workedMinutes || 0;
        chartDays.push({ x: dayLabel(k, period), horas: Number((m / 60).toFixed(1)) });
      }
    }

    const lineData = [];
    if (unit === 'month') {
      for (const ym of monthsOfRange) {
        let punc = 0, total = 0;
        for (const k of Object.keys(byDay)) {
          // Solo cuentan días con clockIn Y con slot configurado (días sin
          // jornada teórica no participan en el % de puntualidad).
          const dayStartMin = byDay[k]._startMin;
          if (k.slice(0, 7) === ym && byDay[k].clockIns[0] && dayStartMin != null) {
            const ciMin = minutesOfDayInZone(byDay[k].clockIns[0], tz);
            if (ciMin <= dayStartMin + 5) punc++;
            total++;
          }
        }
        lineData.push({ x: dayLabel(`${ym}-01`, period), puntualidad: total > 0 ? Math.round((punc / total) * 100) : 100 });
      }
    } else {
      for (let k = start; k && k <= end; k = addDaysToDayKey(k, 1)) {
        const d = byDay[k];
        if (d && d.clockIns[0] && d._startMin != null) {
          const ciMin = minutesOfDayInZone(d.clockIns[0], tz);
          lineData.push({ x: dayLabel(k, period), puntualidad: ciMin <= d._startMin + 5 ? 100 : Math.max(50, 100 - (ciMin - d._startMin) * 2) });
        }
      }
    }

    const workedDays = Object.values(byDay).filter((d) => d.clockIns.length).length;
    const totalCountDays = workedDays + vacDays + sickDays || 1;
    const distribution = [
      { name: 'Trabajado', value: Math.round((workedDays / totalCountDays) * 100), color: '#0EA5E9' },
      { name: 'Vacaciones', value: Math.round((vacDays / totalCountDays) * 100), color: '#F472B6' },
      { name: 'Día completo', value: Math.round((sickDays / totalCountDays) * 100), color: '#DC2626' },
    ].filter((d) => d.value > 0);

    return {
      totals: {
        horas: fmtHours(totalMinutes),
        puntualidad: `${puntualidad}%`,
        jornadas: `${completeDays} / ${Object.keys(byDay).filter((k) => byDay[k].clockIns.length).length}`,
      },
      bars: chartDays,
      line: lineData,
      distribution: distribution.length ? distribution : [{ name: 'Sin datos', value: 100, color: '#94A3B8' }],
    };
  }, [events, period, workhoursByDow, tz]);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex items-center justify-end mb-5">
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                period === p.key ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-ink-muted">
          <Loader2 size={20} className="animate-spin text-primary" />
          <span className="text-sm">Calculando métricas…</span>
        </div>
      ) : (
        <>
          <section className="pb-5 mb-5 border-b border-slate-200">
            {/* "Horas extra" se ha eliminado: su cómputo nunca podía mostrar
                horas a deber (clampeaba a 0) e ignoraba los días no fichados.
                El saldo real —extras incluidas— lo da la Bolsa de horas de
                abajo, con el mismo cómputo que la ficha del trabajador. */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
                  <Clock size={11} className="text-primary" /> Horas trabajadas
                </div>
                <div className="font-display text-2xl font-bold text-primary-darker tabular-nums">{stats.totals.horas}</div>
              </div>
              <div>
                <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
                  <Target size={11} className="text-accent-dark" /> Puntualidad
                </div>
                <div className="font-display text-2xl font-bold text-accent-dark tabular-nums">{stats.totals.puntualidad}</div>
              </div>
              <div>
                <div className="text-xs text-ink-muted mb-1 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} className="text-success" /> Jornadas completadas
                </div>
                <div className="font-display text-2xl font-bold text-success tabular-nums">{stats.totals.jornadas}</div>
              </div>
            </div>
          </section>

          {/* Bolsa de horas — MISMO componente y MISMO cómputo que la ficha del
              trabajador (WorkerDetailPage). Antes esta pantalla calculaba las
              horas por su cuenta con reglas distintas (sin festivos, sin
              vacaciones, ignorando los días no fichados y clampeando el déficit
              a 0 con Math.max), así que NUNCA podía mostrar horas a deber y no
              cuadraba con la bolsa. Ahora hay un solo cálculo y un solo número.
              canManage=false: el trabajador ve su saldo pero no toca ajustes. */}
          <section className="pb-5 mb-5 border-b border-slate-200">
            {scheduleLoading ? (
              <div className="flex items-center gap-2 text-ink-muted py-4">
                <Loader2 size={14} className="animate-spin text-primary" />
                <span className="text-sm">Cargando bolsa…</span>
              </div>
            ) : (
              <HourBalanceCard
                worker={user}
                events={events}
                workhours={scheduleInput}
                holidaysSet={myHolidays}
                eventsError={error}
                canManage={false}
              />
            )}
          </section>

          <div className="flex-1 flex flex-col gap-5 min-h-0">
            <section className="flex-1 min-h-0 bg-white rounded-xl border border-slate-200 p-5 flex flex-col">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Horas trabajadas</div>
              <div className="font-display text-base font-bold text-ink mb-2">Distribución en el período</div>
              <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.bars} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="x" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} unit="h" />
                    <Tooltip cursor={{ fill: '#F1F5F9' }} contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}h`, 'Horas']} />
                    <Bar dataKey="horas" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-5">
              <section className="min-h-0 bg-white rounded-xl border border-slate-200 p-5 flex flex-col">
                <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Puntualidad</div>
                <div className="font-display text-base font-bold text-ink mb-2">Tendencia del período</div>
                <div className="flex-1 min-h-0">
                  {stats.line.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats.line} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="x" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
                        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}%`, 'Puntualidad']} />
                        <Line type="monotone" dataKey="puntualidad" stroke="#F472B6" strokeWidth={2.5} dot={{ fill: '#F472B6', r: 4 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-ink-muted italic">Sin datos en el período</div>
                  )}
                </div>
              </section>

              <section className="min-h-0 bg-white rounded-xl border border-slate-200 p-5 flex flex-col">
                <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Distribución</div>
                <div className="font-display text-base font-bold text-ink mb-2">Reparto del tiempo</div>
                <div className="flex-1 min-h-0 grid grid-cols-[1fr_auto] gap-4 items-center">
                  <div className="min-h-0 h-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={stats.distribution} innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                          {stats.distribution.map((d) => <Cell key={d.name} fill={d.color} stroke="none" />)}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v}%`, '']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="space-y-1.5 text-xs">
                    {stats.distribution.map((d) => (
                      <li key={d.name} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                        <span className="text-ink font-medium">{d.name}</span>
                        <span className="text-ink-muted tabular-nums ml-auto">{d.value}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Generador de informes (admins)
// ─────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  {
    key: 'pdf-rango',
    label: 'PDF por trabajador',
    icon: FileDown,
    help: 'Detalle diario RDL 8/2019 entre dos fechas — una página por persona seleccionada.',
  },
  {
    key: 'xlsx-rango',
    label: 'Excel resumen mensual',
    icon: FileSpreadsheet,
    help: 'Resumen de horas por trabajador y mes en el período indicado — una hoja por empresa.',
  },
  {
    key: 'xlsx-detallado',
    label: 'Excel desglosado por día',
    icon: FileSpreadsheet,
    help: 'Mismo desglose que el PDF (día · entrada · salida · pausa · total) pero en Excel editable — una hoja por trabajador, con su nombre y NIF.',
  },
];

const CheckboxList = ({ items, selected, onToggle, onSelectAll, onClearAll, emptyText, searchable = false }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!searchable || !q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((it) => (it.label || '').toLowerCase().includes(needle));
  }, [items, q, searchable]);

  if (items.length === 0) {
    return <div className="text-xs text-ink-muted italic py-3 px-2">{emptyText}</div>;
  }

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-2 mb-2">
        {searchable && (
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-primary"
            />
          </div>
        )}
        <div className="flex gap-2 text-[11px] font-semibold shrink-0">
          <button type="button" onClick={onSelectAll} className="text-primary hover:underline">Todos</button>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={onClearAll} className="text-ink-muted hover:underline">Limpiar</button>
        </div>
      </div>
      <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
        {filtered.length === 0 ? (
          <div className="text-xs text-ink-muted italic py-3 px-2">Sin resultados.</div>
        ) : filtered.map((it) => {
          const checked = selected.has(it.id);
          return (
            <label
              key={it.id}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer transition-colors ${
                checked ? 'bg-primary-soft/50' : 'hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(it.id)}
                className="accent-primary shrink-0"
              />
              <span className="text-ink truncate">{it.label}</span>
              {it.hint && <span className="text-xs text-ink-muted ml-auto shrink-0">{it.hint}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
};

const ReportGenerator = ({ user }) => {
  // Scope visible para el selector de empresas:
  //   - Super (5) e Inspector (4): TODAS las empresas, con selector.
  //   - Admin empresa (1), Admin sede (2) y Supervisor (3): SOLO su empresa
  //     (auto-seleccionada). El admin sede y el supervisor con workingSite
  //     ven sólo trabajadores de su sede; el supervisor sin workingSite
  //     ve toda la empresa. Eso lo hace filterWorkersForUser via isSedeScoped.
  //   - Trabajador (0): sin selector — sólo informe propio.
  const isMaster = user?.type === USER_TYPES.MECHAGODZILLA;
  const isInspector = user?.type === USER_TYPES.PUNISHER;
  const showCompanySelector = isMaster || isInspector;
  // Roles cuyo informe se limita a sí mismos (sin elegir trabajadores).
  const isSelfOnly = user?.type === 0;
  const now = new Date();

  const [reportType, setReportType] = useState('pdf-rango');
  const isoToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isoMonthAgo = (() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [reportStart, setReportStart] = useState(isoMonthAgo);
  const [reportEnd, setReportEnd] = useState(isoToday);
  const [reportYear, setReportYear] = useState(now.getFullYear());

  const [companies, setCompanies] = useState([]);
  const [workplacesByCompany, setWorkplacesByCompany] = useState({}); // { companyId: [wp] }
  const [workersByCompany, setWorkersByCompany] = useState({});       // { companyId: [w] }
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [selectedSedes, setSelectedSedes] = useState(new Set());
  const [selectedWorkers, setSelectedWorkers] = useState(new Set());

  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingScope, setLoadingScope] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Carga inicial de empresas según rol
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBase(true);
      setError(null);
      try {
        let comps = [];
        if (isMaster || isInspector) {
          // Super e inspector ven todas las empresas con selector.
          comps = await searchCompanies('');
        } else if (user?.companyId) {
          // Admin empresa, admin sede, supervisor y trabajador trabajan
          // siempre sobre su única empresa.
          const all = await searchCompanies('');
          comps = (Array.isArray(all) ? all : []).filter((c) => c.id === user.companyId);
        }
        if (cancelled) return;
        setCompanies(Array.isArray(comps) ? comps : []);
        // Pre-selección automática para roles con scope fijo.
        if (!isMaster && !isInspector && user?.companyId) {
          setSelectedCompanies(new Set([user.companyId]));
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoadingBase(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isMaster, isInspector, user?.companyId]);

  // Para self-only (trabajador / supervisor): cuando ya tenemos la lista
  // de trabajadores de su empresa cargada, auto-seleccionamos al propio user.
  useEffect(() => {
    if (!isSelfOnly || !user?.id) return;
    const myCompanyWorkers = workersByCompany[user?.companyId];
    if (!Array.isArray(myCompanyWorkers)) return;
    const me = myCompanyWorkers.find((w) => w.id === user.id);
    if (me) setSelectedWorkers(new Set([user.id]));
  }, [isSelfOnly, user?.id, user?.companyId, workersByCompany]);

  // Carga de sedes + trabajadores cada vez que cambian las empresas seleccionadas
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(selectedCompanies);
    if (ids.length === 0) {
      setWorkplacesByCompany({});
      setWorkersByCompany({});
      setSelectedSedes(new Set());
      setSelectedWorkers(new Set());
      return;
    }
    (async () => {
      setLoadingScope(true);
      setError(null);
      try {
        const sedeScoped = isSedeScoped(user?.type);
        const userWs = user?.workingSite != null ? String(user.workingSite) : null;
        const results = await Promise.all(ids.map(async (cid) => {
          const [wpsRaw, wsRaw] = await Promise.all([
            getWorkplaces(cid).catch(() => []),
            getCompanyWorkers(cid).catch(() => searchWorkers('').then((all) =>
              (Array.isArray(all) ? all : []).filter((w) => w.companyId === cid))),
          ]);
          let wps = Array.isArray(wpsRaw) ? wpsRaw : [];
          let ws = Array.isArray(wsRaw) ? wsRaw : [];
          // Admins de sede / overseer sólo ven su propia sede y su equipo
          if (sedeScoped && userWs) {
            wps = wps.filter((wp) => String(wp.id) === userWs);
            ws = filterWorkersForUser(ws, user);
          }
          return { cid, wps, ws };
        }));
        if (cancelled) return;
        const wpMap = {};
        const wMap = {};
        for (const r of results) {
          wpMap[r.cid] = r.wps;
          wMap[r.cid] = r.ws;
        }
        setWorkplacesByCompany(wpMap);
        setWorkersByCompany(wMap);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoadingScope(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCompanies, user?.type, user?.workingSite, user?.id]);

  const companyItems = useMemo(() => companies.map((c) => ({
    id: c.id,
    label: c.name || `Empresa #${c.id}`,
    hint: c.cif || undefined,
  })), [companies]);

  // Sedes: unión de sedes de las empresas seleccionadas, prefijadas con nombre de empresa si hay más de una
  const sedeItems = useMemo(() => {
    const items = [];
    const companyName = (cid) => companies.find((c) => c.id === cid)?.name || `Empresa #${cid}`;
    const multi = selectedCompanies.size > 1;
    for (const cid of selectedCompanies) {
      const wps = workplacesByCompany[cid] || [];
      for (const wp of wps) {
        items.push({
          id: wp.id,
          label: multi ? `${companyName(cid)} · ${wp.name || `Sede #${wp.id}`}` : (wp.name || `Sede #${wp.id}`),
          hint: wp.city || undefined,
          companyId: cid,
        });
      }
    }
    return items;
  }, [workplacesByCompany, selectedCompanies, companies]);

  // Lista de trabajadores candidatos (por empresa y, si hay filtro de sedes, por workingSite)
  const workerItems = useMemo(() => {
    const items = [];
    const hasSedeFilter = selectedSedes.size > 0;
    const multi = selectedCompanies.size > 1;
    const companyName = (cid) => companies.find((c) => c.id === cid)?.name || `Empresa #${cid}`;
    for (const cid of selectedCompanies) {
      const ws = workersByCompany[cid] || [];
      for (const w of ws) {
        if (hasSedeFilter) {
          // Multicentro: incluimos al trabajador si ALGUNO de sus centros está
          // entre las sedes seleccionadas. Sin centros → no se filtra por sede.
          const sites = workerSites(w);
          if (sites.length && !sites.some((s) => selectedSedes.has(Number(s)) || selectedSedes.has(s))) continue;
        }
        items.push({
          id: w.id,
          label: multi ? `${companyName(cid)} · ${w.name || `#${w.id}`}` : (w.name || `#${w.id}`),
          hint: w.email || undefined,
          companyId: cid,
          worker: w,
        });
      }
    }
    return items;
  }, [workersByCompany, selectedCompanies, selectedSedes, companies]);

  // Si cambian empresas/sedes, depuramos la selección de trabajadores para no dejar huérfanos
  useEffect(() => {
    setSelectedWorkers((prev) => {
      const validIds = new Set(workerItems.map((it) => it.id));
      const next = new Set();
      for (const id of prev) if (validIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [workerItems]);

  useEffect(() => {
    setSelectedSedes((prev) => {
      const validIds = new Set(sedeItems.map((it) => it.id));
      const next = new Set();
      for (const id of prev) if (validIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [sedeItems]);

  const toggle = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectAll = (items, setter) => () => setter(new Set(items.map((it) => it.id)));
  const clearAll = (setter) => () => setter(new Set());

  // Para self-only consideramos que SIEMPRE hay un trabajador picked (él mismo),
  // aunque no haya selección explícita en la UI.
  const canGenerate = (isSelfOnly || selectedWorkers.size > 0) && !generating && !loadingBase;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      // Construye la lista de "picked workers" según el rol:
      //  - self-only → siempre el propio user, da igual la selección.
      //  - resto     → los seleccionados en la UI.
      const pickedWorkers = isSelfOnly
        ? [{
            id: user.id,
            companyId: user.companyId,
            worker: {
              id: user.id,
              name: user.nombre || user.email,
              email: user.email,
              nif: user.nif,
              ssNumber: user.ssNumber,
              workingSite: user.workingSite,
              type: user.type,
              // Para la zona horaria del informe (Canarias / península).
              province: user.province,
              autonomousCommunity: user.autonomousCommunity,
            },
          }]
        : workerItems.filter((it) => selectedWorkers.has(it.id));

      if (!reportStart || !reportEnd || new Date(reportStart) > new Date(reportEnd)) {
        setError(new Error('Selecciona un período válido (desde / hasta).'));
        setGenerating(false);
        return;
      }
      const startDate = new Date(`${reportStart}T00:00:00`);
      const endDate = new Date(`${reportEnd}T00:00:00`);
      // Sumamos 1 día al final para que el backend ANTIGUO (fin exclusivo a
      // 00:00Z) incluya el día completo, y restamos 1 al inicio porque el día
      // de Madrid empieza a las 22:00/23:00 UTC del anterior. Los eventos se
      // recortan luego a [reportStart, reportEnd] por día EN LA ZONA DE CADA
      // TRABAJADOR (filterEventsToLocalRange).
      const apiStartIso = addDaysToDayKey(reportStart, -1);
      const apiEndIso = addDaysToDayKey(reportEnd, 1);

      if (reportType === 'pdf-rango' || reportType === 'xlsx-detallado') {
        // PDF y Excel desglosado comparten estructura: un entry por
        // trabajador con eventos + jornada. Solo cambia el writer final.
        const entries = await Promise.all(pickedWorkers.map(async (it) => {
          const [events, wh] = await Promise.all([
            getEventsByRange(it.id, apiStartIso, apiEndIso).catch(() => []),
            getWorkHours(it.id).catch(() => []),
          ]);
          const workerTz = timeZoneForWorker(it.worker);
          const wpId = it.worker?.workingSite;
          const workplaceName = wpId != null
            ? (workplacesByCompany[it.companyId] || []).find((w) => Number(w.id) === Number(wpId))?.name || null
            : null;
          return {
            worker: it.worker,
            company: companies.find((c) => c.id === it.companyId) || null,
            workplaceName,
            events: filterEventsToLocalRange(events, reportStart, reportEnd, workerTz),
            // Días y horas del informe en la zona del trabajador.
            timeZone: workerTz,
            // Ya no se usa en reports.js (todas las pausas se restan,
            // decisión 2026-09-21); se sigue pasando por compatibilidad.
            workhoursByDow: buildWorkHoursByDow(Array.isArray(wh) ? wh : []),
          };
        }));
        if (reportType === 'pdf-rango') {
          generateBulkRangePdf({ entries, startDate, endDate });
          setSuccess(`PDF generado: ${entries.length} trabajador(es), ${reportStart} → ${reportEnd}.`);
        } else {
          generateBulkRangeExcelDetailed({ entries, startDate, endDate });
          setSuccess(`Excel desglosado generado: ${entries.length} hoja(s) — una por trabajador, ${reportStart} → ${reportEnd}.`);
        }
      } else {
        // Excel por rango — agrupamos por empresa.
        const companiesData = [];
        const groupCompanyIds = isSelfOnly ? [user.companyId] : Array.from(selectedCompanies);
        for (const cid of groupCompanyIds) {
          const workersOfC = pickedWorkers.filter((it) => it.companyId === cid).map((it) => it.worker);
          if (workersOfC.length === 0) continue;
          const eventsByWorker = {};
          const workhoursByWorker = new Map();
          await Promise.all(workersOfC.map(async (w) => {
            try {
              const [evs, wh] = await Promise.all([
                getEventsByRange(w.id, apiStartIso, apiEndIso),
                getWorkHours(w.id).catch(() => []),
              ]);
              eventsByWorker[w.id] = filterEventsToLocalRange(evs, reportStart, reportEnd, timeZoneForWorker(w));
              workhoursByWorker.set(w.id, buildWorkHoursByDow(Array.isArray(wh) ? wh : []));
            } catch {
              eventsByWorker[w.id] = [];
            }
          }));
          const companyMatch = companies.find((c) => c.id === cid);
          companiesData.push({
            company: companyMatch || { name: isSelfOnly ? 'Mi empresa' : `Empresa #${cid}` },
            workers: workersOfC,
            eventsByWorker,
            workhoursByWorker,
          });
        }
        if (companiesData.length === 0) {
          setError(new Error('Ningún trabajador seleccionado.'));
          setGenerating(false);
          return;
        }
        generateBulkRangeExcel({ companiesData, startDate, endDate });
        const total = companiesData.reduce((s, c) => s + c.workers.length, 0);
        setSuccess(`Excel generado: ${total} trabajador(es), ${reportStart} → ${reportEnd}.`);
      }
    } catch (err) {
      setError(err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-y-auto pr-1">
      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message || 'Error al generar el informe.'}</div>
        </div>
      )}
      {success && (
        <div className="border border-success/30 bg-success/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{success}</div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="space-y-5">
          {/* Tipo de informe */}
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">1 · Tipo de informe</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {REPORT_TYPES.map((t) => {
                const Icon = t.icon;
                const active = reportType === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setReportType(t.key)}
                    className={`text-left rounded-xl border p-3 transition-colors ${
                      active ? 'border-primary bg-primary-soft/30' : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={16} className={active ? 'text-primary-darker' : 'text-ink-muted'} />
                      <span className={`text-sm font-semibold ${active ? 'text-primary-darker' : 'text-ink'}`}>{t.label}</span>
                    </div>
                    <div className="text-xs text-ink-muted">{t.help}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Período (común a PDF y Excel ahora) */}
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">2 · Período</div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Desde</label>
                <input
                  type="date"
                  value={reportStart}
                  max={reportEnd || undefined}
                  onChange={(e) => setReportStart(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Hasta</label>
                <input
                  type="date"
                  value={reportEnd}
                  min={reportStart || undefined}
                  onChange={(e) => setReportEnd(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Empresas — visible para super e inspector */}
          {!isSelfOnly && showCompanySelector && (
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 inline-flex items-center gap-1.5">
                <Building2 size={12} /> 3 · Empresas
              </div>
              {loadingBase ? (
                <div className="text-xs text-ink-muted py-3 inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando empresas…
                </div>
              ) : (
                <CheckboxList
                  items={companyItems}
                  selected={selectedCompanies}
                  onToggle={toggle(setSelectedCompanies)}
                  onSelectAll={selectAll(companyItems, setSelectedCompanies)}
                  onClearAll={clearAll(setSelectedCompanies)}
                  emptyText="Sin empresas registradas."
                  searchable
                />
              )}
            </div>
          )}
        </section>

        {/* Selectores de sedes y trabajadores — ocultos para self-only */}
        {!isSelfOnly && (
          <section className="space-y-5">
            {/* Sedes */}
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 inline-flex items-center gap-1.5">
                <MapPin size={12} /> {showCompanySelector ? '4' : '3'} · Sedes <span className="normal-case text-ink-muted/70 font-normal">(opcional, filtra trabajadores)</span>
              </div>
              {loadingScope ? (
                <div className="text-xs text-ink-muted py-3 inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando sedes…
                </div>
              ) : (
                <CheckboxList
                  items={sedeItems}
                  selected={selectedSedes}
                  onToggle={toggle(setSelectedSedes)}
                  onSelectAll={selectAll(sedeItems, setSelectedSedes)}
                  onClearAll={clearAll(setSelectedSedes)}
                  emptyText={selectedCompanies.size === 0 ? 'Selecciona una empresa para ver sus sedes.' : 'Esta empresa no tiene sedes registradas.'}
                />
              )}
            </div>

            {/* Trabajadores */}
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 inline-flex items-center gap-1.5">
                <Users size={12} /> {showCompanySelector ? '5' : '4'} · Trabajadores
                <span className="normal-case text-ink-muted/70 font-normal">({selectedWorkers.size} seleccionados)</span>
              </div>
              {loadingScope ? (
                <div className="text-xs text-ink-muted py-3 inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando trabajadores…
                </div>
              ) : (
                <CheckboxList
                  items={workerItems}
                  selected={selectedWorkers}
                  onToggle={toggle(setSelectedWorkers)}
                  onSelectAll={selectAll(workerItems, setSelectedWorkers)}
                  onClearAll={clearAll(setSelectedWorkers)}
                  emptyText={selectedCompanies.size === 0 ? 'Selecciona una empresa para ver sus trabajadores.' : 'Sin trabajadores que coincidan con el filtro.'}
                  searchable
                />
              )}
            </div>
          </section>
        )}
      </div>

      <div className="border-t border-slate-200 mt-6 pt-5 flex items-center justify-between gap-4">
        <div className="text-sm text-ink-muted">
          {selectedWorkers.size === 0
            ? 'Selecciona al menos un trabajador para generar el informe.'
            : `Listos para generar: ${selectedWorkers.size} trabajador(es).`}
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-ink text-white hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating
            ? <><Loader2 size={15} className="animate-spin" /> Generando…</>
            : reportType === 'pdf-rango'
              ? <><FileDown size={15} /> Generar PDF</>
              : <><FileSpreadsheet size={15} /> Generar Excel</>}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Página Informes — pestañas según rol
// ─────────────────────────────────────────────────────────────
const InformesPage = () => {
  const { user } = useAuth();
  // La pestaña 'Generar informes' ahora la ven todos los roles (incluido
  // trabajador y supervisor). El scope visible se decide dentro del
  // componente ReportGenerator según el rol.
  const canGenerate = can(user?.type, 'generateReports');
  const [tab, setTab] = useState('horario');

  const titles = {
    horario: canGenerate ? 'Informe horario por trabajador' : 'Tus fichajes',
    generar: 'Generar PDF y Excel',
    actividad: 'Mi actividad y tendencias',
  };

  // 'Consulta rápida' fusionada en 'Informe horario': este último ahora
  // soporta multi-select de trabajadores, sede, filtros y editar/eliminar
  // fichajes inline. No tiene sentido mantener dos pestañas casi idénticas.
  const tabs = [
    { key: 'horario', label: 'Informe horario' },
    ...(canGenerate ? [{ key: 'generar', label: 'Generar informes' }] : []),
    { key: 'actividad', label: 'Mi actividad' },
  ];

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Informes</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">{titles[tab]}</h1>
        </div>
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                tab === t.key ? 'bg-white text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'horario' && <InformeHorarioView user={user} />}
      {tab === 'generar' && canGenerate && <ReportGenerator user={user} />}
      {tab === 'actividad' && <PersonalActivity user={user} />}
    </div>
  );
};

export default InformesPage;
